import type { FeedMetricRequest, MetricDataRequest } from '../../api/dispatchMetrics';
import type { DispatchMetric } from '../../api/types';
import { isFeedCategory, isSnapshotMetric, metricTypeOf } from './metricMeta';

/**
 * What to ask dispatch for, worked out once for the whole page.
 *
 * This is what the page-level window buys. With the window per METRIC, twelve
 * cards were twelve requests because no two of them were asking about the same
 * period. With one window, every card of a category differs only in which
 * subcategories it draws — so the union of those subcategories answers all of
 * them, and the page costs about one request per category instead.
 *
 * Three batches come out of it rather than one, because they are three
 * different questions:
 *
 * - `window` is the frame the operator chose;
 * - `compare` is the window immediately before it, and exists only if some KPI
 *   on the page shows a percentage change — a page of charts never asks for it;
 * - `snapshot` carries no window at all. The all-time figures (pending jobs,
 *   outstanding payables) answer `period: "all"`, and narrowing them by a date
 *   range would quietly turn "what is outstanding" into "what became
 *   outstanding in September", which is a different number under the same word.
 *
 * The `dispatch` category is in none of them: it is counted from the job board
 * rather than read from this API, and `feed` is that fourth question. It names
 * each metric rather than unioning a category, because a dispatch card's figure
 * is a union over ITS OWN buckets — a job that is both a farm-out and a
 * multi-stop counts once — and a per-category union cannot be split back into
 * cards without counting that job twice. It still costs one upstream read for
 * the whole page.
 */

export interface ReadPlan {
  window: MetricDataRequest[];
  compare: MetricDataRequest[];
  snapshot: MetricDataRequest[];
  /**
   * Everything the WINDOW batch asks for, read again over a wider range.
   *
   * Used only when the page's window is a single day, where a chart has one
   * bar and no shape — see `chartContext`. It covers the KPI subcategories as
   * well as the chart ones, because a KPI's sparkline has exactly the same
   * problem; the KPI's own FIGURE still comes from the window batch, so the
   * number on the card is the day the operator asked for and nothing else.
   */
  context: MetricDataRequest[];
  /** The dispatch cards, each with the buckets it draws. */
  feed: FeedMetricRequest[];
}

const addTo = (into: Map<string, Set<string>>, category: string, codes: string[]) => {
  const key = category.toLowerCase();
  const existing = into.get(key) ?? new Set<string>();
  for (const code of codes) if (code) existing.add(code);
  into.set(key, existing);
};

const toRequests = (from: Map<string, Set<string>>): MetricDataRequest[] =>
  [...from.entries()]
    .filter(([, codes]) => codes.size > 0)
    .map(([category, codes]) => ({ category, subcategories: [...codes] }));

export const planReads = (metrics: DispatchMetric[]): ReadPlan => {
  const window = new Map<string, Set<string>>();
  const compare = new Map<string, Set<string>>();
  const snapshot = new Map<string, Set<string>>();
  const feed: FeedMetricRequest[] = [];

  for (const metric of metrics) {
    if (isFeedCategory(metric.category)) {
      feed.push({ id: metric.id, subcategories: metric.subCategory.filter(Boolean) });
      continue;
    }
    const codes = metric.subCategory.filter(Boolean);
    if (!codes.length) continue;

    if (isSnapshotMetric(codes)) {
      addTo(snapshot, metric.category, codes);
      continue;
    }

    addTo(window, metric.category, codes);
    // Only a KPI draws a change; a chart shows the shape of the window itself
    // and has nothing to say about the one before it.
    if (metricTypeOf(metric.type) === 'kpi') addTo(compare, metric.category, codes);
  }

  return {
    window: toRequests(window),
    compare: toRequests(compare),
    snapshot: toRequests(snapshot),
    // The same question over a wider range; the caller asks it only on a
    // one-day window, so on every other frame this costs nothing.
    context: toRequests(window),
    feed,
  };
};

/**
 * A stable key for a batch, so the loader refetches when the question changed
 * and not when the array identity did. Sorted, because two renders may union
 * the same subcategories in a different order and that is not a new question.
 */
export const planKey = (requests: MetricDataRequest[]): string =>
  requests
    .map((request) => `${request.category}:${[...request.subcategories].sort().join(',')}`)
    .sort()
    .join('|');

/** The same, for the feed batch, which is keyed on the cards rather than a category. */
export const feedKey = (requests: FeedMetricRequest[]): string =>
  requests
    .map((request) => `${request.id}:${[...request.subcategories].sort().join(',')}`)
    .sort()
    .join('|');
